import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Building2, Boxes, ShieldCheck, Bot, Fingerprint, KeyRound, Scale,
  FileLock2, Activity, GitBranch, Server, Archive, X, ArrowUpRight,
} from "lucide-react";

// node-and-link security universe: Organization → Assets → Edge → Defense Wall →
// Identity → Authentication → Authorization → Policy → Risk → Decision → System → Audit
const NODES = [
  { id: "org", label: "Organization", icon: Building2, x: 90, y: 70, tone: "#60a5fa", route: "/app/organizations", detail: "Tenant boundary. Every query is scoped server-side to this organization — no cross-tenant access." },
  { id: "assets", label: "Protected Assets", icon: Boxes, x: 90, y: 210, tone: "#60a5fa", route: "/app/assets", detail: "Only verified + connected + configured assets show SHIELDX PROTECTED. A URL alone never becomes an asset." },
  { id: "edge", label: "ShieldX Edge", icon: Server, x: 270, y: 140, tone: "#38bdf8", route: "/app/website-resilience", detail: "Multi-edge entry. Origin stays private; direct-origin access is blocked. The authorization boundary never moves." },
  { id: "wall", label: "Defense Wall", icon: ShieldCheck, x: 430, y: 70, tone: "#34d399", route: "/app/defense-wall", detail: "Observe → classify → rate-limit → risk. Autonomous ≠ trusted. Capability ≠ authority." },
  { id: "identity", label: "Identity", icon: Fingerprint, x: 430, y: 210, tone: "#a78bfa", route: "/app/login-guard", detail: "Who is requesting? Source classification: human / authorized AI / suspicious automation / unknown." },
  { id: "authn", label: "Authentication", icon: KeyRound, x: 590, y: 140, tone: "#a78bfa", route: "/app/hardware-authz", detail: "Passkey / FIDO2 / hardware-backed. A digital session cannot authorize itself." },
  { id: "authz", label: "Authorization", icon: Scale, x: 740, y: 70, tone: "#fbbf24", route: "/app/authorization", detail: "Scoped, time-limited, revocable. No authorization / expired / revoked / out-of-scope → DENY." },
  { id: "policy", label: "Policy", icon: FileLock2, x: 740, y: 210, tone: "#fbbf24", route: "/app/policies", detail: "Deterministic policy enforcement. Versioned, auditable, server-side authoritative." },
  { id: "risk", label: "Risk + Consensus", icon: Activity, x: 880, y: 140, tone: "#f59e0b", route: "/app/consensus", detail: "3-engine consensus (network / behavior / crypto). Fail-closed. Review is never approval." },
  { id: "decision", label: "Decision", icon: GitBranch, x: 1010, y: 70, tone: "#f87171", route: "/app/security-pipeline", detail: "ALLOW / CHALLENGE / DENY / BLOCK / ISOLATE. Default deny. AI advisory only." },
  { id: "system", label: "Protected System", icon: Server, x: 1010, y: 210, tone: "#34d399", route: "/app/assets", detail: "The protected service — only reached after the full pipeline authorizes within scope." },
  { id: "audit", label: "Audit / Evidence", icon: Archive, x: 1130, y: 140, tone: "#67e8f9", route: "/app/security-pipeline", detail: "Tamper-evident hash-chained audit vault. Every decision traceable to Event + Policy + Authorization IDs." },
];
const LINKS = [
  ["org", "edge"], ["assets", "edge"], ["edge", "wall"], ["edge", "identity"],
  ["wall", "authn"], ["identity", "authn"], ["authn", "authz"], ["authn", "policy"],
  ["authz", "risk"], ["policy", "risk"], ["risk", "decision"], ["decision", "system"],
  ["decision", "audit"], ["system", "audit"],
];
const byId = Object.fromEntries(NODES.map((n) => [n.id, n]));

export default function SecurityTopology({ kpis = {} }) {
  const [sel, setSel] = useState(null);
  const nav = useNavigate();
  const badge = {
    org: kpis.organizations, assets: `${kpis.protected_assets ?? 0}/${kpis.total_assets ?? 0}`,
    risk: kpis.current_risk, decision: kpis.blocked_attacks, wall: kpis.active_threats,
  };

  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-800 bg-[#070b14]" data-testid="security-topology">
      <div className="flex items-center justify-between border-b border-slate-800 px-5 py-3.5">
        <div>
          <h3 className="font-display text-sm font-semibold tracking-wide text-slate-100">ShieldX Security Universe</h3>
          <p className="mt-0.5 text-xs text-slate-500">Click any node to inspect the layer · deterministic boundary, left → right</p>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-600">Topology</span>
      </div>

      <div className="relative">
        <svg viewBox="0 0 1200 290" className="w-full" style={{ maxHeight: 360 }}>
          <defs>
            <radialGradient id="grid-fade" cx="50%" cy="50%" r="75%">
              <stop offset="0%" stopColor="#0b1222" /><stop offset="100%" stopColor="#070b14" />
            </radialGradient>
          </defs>
          <rect width="1200" height="290" fill="url(#grid-fade)" />
          {LINKS.map(([a, b], i) => {
            const na = byId[a], nb = byId[b];
            const active = sel && (sel === a || sel === b);
            return (
              <motion.line key={i} x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
                stroke={active ? "#3b82f6" : "#1e293b"} strokeWidth={active ? 2 : 1.2}
                initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 + i * 0.05 }} />
            );
          })}
          {NODES.map((n, i) => {
            const active = sel === n.id;
            return (
              <motion.g key={n.id} style={{ cursor: "pointer" }} onClick={() => setSel(n.id)}
                initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35, delay: 0.1 + i * 0.05 }}>
                {active && <circle cx={n.x} cy={n.y} r={34} fill={n.tone} opacity={0.12} />}
                <circle cx={n.x} cy={n.y} r={24} fill="#0f172a" stroke={active ? n.tone : "#1e293b"} strokeWidth={active ? 2.5 : 1.5} />
                <circle cx={n.x} cy={n.y} r={24} fill="none" stroke={n.tone} strokeWidth={1} opacity={active ? 0.9 : 0.35} />
                <foreignObject x={n.x - 12} y={n.y - 12} width={24} height={24}>
                  <div className="flex h-6 w-6 items-center justify-center"><n.icon size={15} color={n.tone} /></div>
                </foreignObject>
                <text x={n.x} y={n.y + 40} textAnchor="middle" fontSize="11" fill={active ? "#e2e8f0" : "#94a3b8"} fontFamily="Inter, sans-serif">{n.label}</text>
                {badge[n.id] != null && badge[n.id] !== "" && (
                  <>
                    <circle cx={n.x + 18} cy={n.y - 18} r={9} fill={n.tone} />
                    <text x={n.x + 18} y={n.y - 14.5} textAnchor="middle" fontSize="9" fontWeight="700" fill="#07111f" fontFamily="JetBrains Mono">{String(badge[n.id]).slice(0, 4)}</text>
                  </>
                )}
              </motion.g>
            );
          })}
        </svg>

        {sel && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}
            className="absolute right-3 top-3 w-72 rounded-lg border border-slate-700 bg-slate-900/95 p-4 shadow-2xl backdrop-blur" data-testid="topology-panel">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                {(() => { const Ic = byId[sel].icon; return <Ic size={16} color={byId[sel].tone} />; })()}
                <span className="font-display text-sm font-semibold text-slate-100">{byId[sel].label}</span>
              </div>
              <button onClick={() => setSel(null)} className="text-slate-500 hover:text-slate-300" data-testid="topology-close"><X size={15} /></button>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-slate-400">{byId[sel].detail}</p>
            <button onClick={() => nav(byId[sel].route)} data-testid="topology-open"
              className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md border border-blue-500/40 bg-blue-600/15 px-3 py-1.5 text-xs font-semibold text-blue-200 hover:bg-blue-600/25">
              Open module <ArrowUpRight size={13} />
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
